import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";

const UnpaidDebtDialog = ({ open, onClose, debtAmount, debtCount, debtDetails, onPayDebt }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullWidth 
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 2,
          m: isMobile ? 2 : 3,
        }
      }}
    >
      <DialogTitle sx={{ 
        textAlign: 'center',
        pt: 3,
        pb: 2,
      }}>
        <Typography variant={isMobile ? "h6" : "h5"} fontWeight="600" color="error.main">
          🚫 Nieopłacone zajęcia
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 2, pb: 3 }}>
        <Stack spacing={2.5}>
          <Alert severity="error" icon="⚠️" sx={{ borderRadius: 2 }}>
            <Typography variant="body2" fontWeight="500">
              Musisz zapłacić za poprzednie nieobecne zajęcia przed zapisaniem się na nowe.
            </Typography>
          </Alert>
          
          <Card elevation={0} sx={{ backgroundColor: '#ffebee', border: '2px solid #ef5350' }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Kwota do zapłaty
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" color="error">
                    {debtAmount} PLN
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Liczba zajęć
                  </Typography>
                  <Chip 
                    label={debtCount} 
                    color="error"
                    sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}
                  />
                </Box>
              </Stack>
            </CardContent>
          </Card>
          
          <Box>
            <Typography variant="body2" fontWeight="600" gutterBottom sx={{ mb: 1.5 }}>
              📋 Szczegóły nieopłaconych zajęć:
            </Typography>
            
            <Stack 
              spacing={1} 
              sx={{ 
                maxHeight: 240, 
                overflowY: 'auto',
                pr: 1,
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#ccc',
                  borderRadius: '4px',
                },
              }}
            >
              {debtDetails.map((lesson, index) => (
                <Card key={index} elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="body2" fontWeight="500">
                          {lesson.lesson_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          📅 {new Date(lesson.lesson_date).toLocaleDateString("pl-PL")}
                        </Typography>
                      </Box>
                      <Chip 
                        label={`${lesson.price} PLN`}
                        size="small"
                        color="error"
                        variant="outlined"
                      />
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </Box>
          
          <Alert severity="info" icon="💡" sx={{ borderRadius: 2 }}>
            <Typography variant="caption">
              Możesz zapłacić online lub skontaktować się z instruktorem.
            </Typography>
          </Alert>
        </Stack>
      </DialogContent>
      
      <DialogActions sx={{ 
        px: 3, 
        pb: 3, 
        gap: 1,
        flexDirection: isMobile ? 'column' : 'row'
      }}>
        <Button 
          onClick={onClose} 
          variant="outlined"
          fullWidth={isMobile}
          sx={{ minHeight: 44 }}
        >
          Anuluj
        </Button>
        <Button 
          onClick={onPayDebt} 
          color="success" 
          variant="contained"
          fullWidth={isMobile}
          sx={{ minHeight: 44 }}
        >
          Zapłać online ({debtAmount} PLN)
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UnpaidDebtDialog;
